/**
 * useDiscoverDeals - Hook for fetching discoverable deals for creators
 *
 * NOW USES: rpc_discovery_best_matches RPC for feed results with Best Match Backoff.
 * Facets remain from rpc_get_discover_facets_v3.
 *
 * Single unified query path for Discovery feed.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { debugLog } from '@/lib/debug';
import {
  type DiscoverFilters,
  type FacetResponse,
  type FacetResponseV3,
  type TaxonomyCoverage,
  type V3GateResult,
  type NormalizedIndustryOption,
  DEFAULT_FILTERS,
  getDiscoverFacetsV3,
  getUserAdoptedDealIds,
  getUserKatalogId,
  getV3GateStatus,
  getAllIndustries,
  searchParamsToFilters,
  filtersToSearchParams,
  toggleArrayFilter,
  updateFilter,
  resetFilters,
} from '@bright-web-skeleton/discover-contract';
import type { ImageVariant } from '@bright-web-skeleton/ui';

// Re-export types for backwards compatibility
export type {
  DiscoverFilters,
  FacetResponse,
  FacetResponseV3,
  TaxonomyCoverage,
  NormalizedIndustryOption,
} from '@bright-web-skeleton/discover-contract';

/**
 * DiscoverDeal - UI-friendly deal type with computed display fields
 * Extended with Best Match Backoff metadata and canonical money fields
 */
export interface DiscoverDeal {
  id: string;
  title: string;
  description: string | null;
  brandId: string;
  brandName: string;
  brandAvatar: string | null;
  brandHandle: string | null;
  brandStoreId?: string | null;
  brandStoreSlug?: string | null;
  format: 'single' | 'campaign';
  type: 'products' | 'services' | 'events' | 'other';
  commissionRate: number;
  discountValue: number;
  coverImageUrl: string | null;
  displayImageUrl: string | null;
  videoUrl: string | null;
  country: string | null;
  industry: string | null;
  primaryTag: string | null;
  status: string;
  startsAt: string | null;
  expiresAt: string | null;
  maxUses: number | null;
  currentUses: number | null;
  isJoined: boolean;
  productCount: number;
  shippingPrice: number | null;
  originalPrice: number | null;
  storeName: string | null;
  storePlatform: 'shopify' | 'woocommerce' | 'custom' | null;
  primaryMediaType: string | null;
  primaryMediaRef: string | null;
  isPrimaryVideo: boolean;
  kloutscore: number | null;
  kloutscoreVerified: boolean;
  imageVariant: ImageVariant;
  // Canonical money fields
  effectivePriceAmount: number | null;
  listPriceAmount: number | null;
  discountAmount: number | null;
  creatorEarningsAmount: number | null;
  profitAmount: number | null;
  extraAmount: number | null;
  rewardTokens: number | null;
  // Taxonomy names (for resolver)
  industryName: string | null;
  categoryName: string | null;
  subcategoryName: string | null;
  // Best Match Backoff metadata
  matchTier: number;
  matchScore: number;
  isCloseMatch: boolean;
  isSimilarCategory: boolean;
  outsidePrice: boolean;
  outsideCommission: boolean;
  outsideDiscount: boolean;
  // Enriched discovery metadata
  accessRule: string | null;
  dealMode: string | null;
  storeRegion: string | null;
  storeCountryCode: string | null;
  // Brand-level attribution (raw — mapper resolves it via resolvePublicRoleIdentity)
  attributionRole: 'connector' | 'general_agent' | 'agent' | 'partner' | null;
  attributionUserId: string | null;
  attributionHandle: string | null;
  attributionDisplayName: string | null;
  attributionFocus: string | null;
  attributionBackground: string | null;
  attributionStatus: string | null;
  attributionActivationStatus: string | null;
}

interface UseDiscoverDealsResult {
  deals: DiscoverDeal[];
  facets: FacetResponse;
  facetsV3: FacetResponseV3 | null;
  /** All available industries (independent of results) for escape hatch - normalized shape */
  allIndustries: NormalizedIndustryOption[];
  total: number;
  loading: boolean;
  error: string | null;
  filters: DiscoverFilters;
  useV3: boolean;
  coverage: TaxonomyCoverage | null;
  v3FiltersStripped: boolean;
  /** True if any results have relaxed matching (tier > 0 or outside_* flags) */
  hasRelaxedResults: boolean;
  /** Count of deals with tier > 0 */
  relaxedCount: number;
  setFilters: (filters: DiscoverFilters) => void;
  updateFilter: <K extends keyof DiscoverFilters>(key: K, value: DiscoverFilters[K]) => void;
  toggleArrayFilter: (
    key:
      | 'subjects'
      | 'geo'
      | 'brand_categories'
      | 'psychographics'
      | 'brand_geographic_focus'
      | 'industry_ids'
      | 'category_ids'
      | 'subcategory_ids',
    value: string,
  ) => void;
  resetFilters: () => void;
  refetch: () => Promise<void>;
}

/**
 * Map RPC result row to UI DiscoverDeal
 */
let _debugLoggedFirst = false;
function mapRpcRowToUI(row: any, isJoined: boolean): DiscoverDeal {
  // Debug: log first deal's media fields when ?debugCards=1
  if (!_debugLoggedFirst && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debugCards')) {
    _debugLoggedFirst = true;
    debugLog('debugCards:FirstRPCRow', { keys: Object.keys(row) });
    debugLog('debugCards:MediaFields', {
      cover_image_url: row.cover_image_url,
      primary_media_ref: row.primary_media_ref,
      primary_media_type: row.primary_media_type,
      video_url: row.video_url,
      resolved_media: row.resolved_media,
      brand_avatar_url: row.brand_avatar_url,
    });
  }

  const country = row.geo_filters?.[0] || null;
  const industry = row.subject_filters?.[0] || null;
  const primaryTag = industry || null;

  // Media resolution — video thumbnail FIRST for video deals
  let isPrimaryVideo = false;
  let displayImageUrl: string | null = null;

  if (row.video_url) {
    isPrimaryVideo = true;
    // Derive YouTube/Shorts thumbnail so the card shows the real video preview
    const ytMatch = row.video_url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]+)/
    );
    if (ytMatch) {
      displayImageUrl = `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
    }
  }

  // Fallback to standard image fields only if no video thumbnail was derived
  if (!displayImageUrl) {
    displayImageUrl = row.resolved_media || row.primary_media_ref || row.cover_image_url || null;
  }

  const deriveDealType = (): 'products' | 'services' | 'events' | 'other' => {
    if (row.subject_filters && row.subject_filters.length > 0) {
      const firstFilter = row.subject_filters[0].toLowerCase();
      if (firstFilter.includes('service')) return 'services';
      if (firstFilter.includes('event')) return 'events';
    }
    return 'products';
  };

  // Kloutscore: use real value from DB if available, otherwise null (no fabrication)
  const baseScore = row.kloutscore ?? null;

  // Determine close match status
  const outsidePrice = row.outside_price ?? false;
  const outsideCommission = row.outside_commission ?? false;
  const outsideDiscount = row.outside_discount ?? false;
  const relaxedTaxonomy = row.relaxed_taxonomy ?? false;
  const matchTier = row.match_tier ?? 0;

  // Compute canonical money fields
  const commissionRate = row.commission_rate || 0;
  const discountRate = row.discount_value ? row.discount_value / 100 : 0;
  
  // Use cents-based pricing if available
  const hasCentsPricing = row.sale_cents != null || row.msrp_cents != null;
  
  let effectivePrice: number | null;
  let listPrice: number | null;
  let discountAmount: number | null;
  let creatorEarnings: number | null;
  
  if (hasCentsPricing) {
    effectivePrice = row.sale_cents != null ? row.sale_cents / 100 : (row.price_min || row.total || null);
    listPrice = row.msrp_cents != null ? row.msrp_cents / 100 : effectivePrice;
    discountAmount = listPrice != null && effectivePrice != null && listPrice > effectivePrice
      ? listPrice - effectivePrice : null;
    const creatorCommPct = row.creator_commission_pct ?? (commissionRate * 100);
    const creatorBonusCents = row.creator_bonus_cents ?? 0;
    creatorEarnings = effectivePrice != null && creatorCommPct > 0
      ? (effectivePrice * creatorCommPct / 100) + (creatorBonusCents / 100)
      : creatorBonusCents > 0 ? creatorBonusCents / 100 : null;
  } else {
    effectivePrice = row.price_min || row.total || null;
    listPrice = effectivePrice && discountRate > 0 ? effectivePrice / (1 - discountRate) : effectivePrice;
    discountAmount = listPrice && effectivePrice && discountRate > 0 ? listPrice - effectivePrice : null;
    creatorEarnings = effectivePrice && commissionRate > 0 ? effectivePrice * commissionRate : null;
  }

  return {
    id: row.deal_id,
    title: row.title || '',
    description: row.description,
    brandId: row.brand_id,
    brandName: row.brand_name || 'Unknown',
    brandAvatar: row.brand_avatar_url,
    brandHandle: row.brand_handle || null,
    brandStoreId: row.brand_store_id ?? null,
    brandStoreSlug: row.brand_store_slug ?? null,
    format: (row.product_count || 0) > 1 ? 'campaign' : 'single',
    type: deriveDealType(),
    commissionRate,
    discountValue: row.discount_value || 0,
    coverImageUrl: row.cover_image_url || null,
    displayImageUrl,
    videoUrl: row.video_url || null,
    country,
    industry,
    primaryTag,
    status: row.status || 'published',
    startsAt: row.starts_at,
    expiresAt: row.expires_at,
    maxUses: null,
    currentUses: null,
    isJoined,
    productCount: Number(row.product_count) || 0,
    shippingPrice: row.shipping_price || null,
    originalPrice: listPrice,
    storeName: null,
    storePlatform:
      row.store_platform === 'shopify' ||
      row.store_platform === 'woocommerce' ||
      row.store_platform === 'custom'
        ? row.store_platform
        : null,
    primaryMediaType: row.primary_media_type || null,
    primaryMediaRef: row.primary_media_ref || null,
    isPrimaryVideo,
    kloutscore: baseScore,
    kloutscoreVerified: baseScore !== null && baseScore >= 70,
    imageVariant: (row.hero_image_variant as ImageVariant) || 'square',
    // Canonical money fields
    effectivePriceAmount: effectivePrice,
    listPriceAmount: listPrice,
    discountAmount,
    creatorEarningsAmount: creatorEarnings,
    profitAmount: null,
    extraAmount: null,
    rewardTokens: null,
    // Taxonomy names - enriched after fetch
    industryName: null,
    categoryName: null,
    subcategoryName: null,
    // Best Match Backoff metadata
    matchTier,
    matchScore: row.score ?? 0,
    isCloseMatch: matchTier > 0 || outsidePrice || outsideCommission || outsideDiscount,
    isSimilarCategory: relaxedTaxonomy,
    outsidePrice,
    outsideCommission,
    outsideDiscount,
    // Enriched discovery metadata
    accessRule: row.access_rule || null,
    dealMode: row.deal_mode || null,
    storeRegion: row.store_region || null,
    storeCountryCode: row.store_country_code || null,
    // Brand-level attribution (raw — mapper resolves overlay via resolvePublicRoleIdentity)
    attributionRole: (row.attribution_role as DiscoverDeal['attributionRole']) ?? null,
    attributionUserId: row.attribution_user_id ?? null,
    attributionHandle: row.attribution_handle ?? null,
    attributionDisplayName: row.attribution_display_name ?? null,
    attributionFocus: row.attribution_focus ?? null,
    attributionBackground: row.attribution_background ?? null,
    attributionStatus: row.attribution_status ?? null,
    attributionActivationStatus: row.attribution_activation_status ?? null,
  };
}

// V3 feature flag threshold
const V3_COVERAGE_THRESHOLD = 50;
const FORCE_V3 = false;

/**
 * Build RPC context from filters
 */
function buildRpcContext(filters: DiscoverFilters): object {
  return {
    query: filters.q || null,
    industryIds: filters.industry_ids?.length ? filters.industry_ids : null,
    categoryIds: filters.category_ids?.length ? filters.category_ids : null,
    subcategoryIds: filters.subcategory_ids?.length ? filters.subcategory_ids : null,
    priceMin: filters.price_min ?? null,
    priceMax: filters.price_max ?? null,
    commissionMin: filters.commission_min ?? null,
    commissionMax: filters.commission_max ?? null,
    discountMin: filters.discount_min ?? null,
    discountMax: filters.discount_max ?? null,
  };
}

/**
 * Hook for fetching discoverable deals with Best Match Backoff ranking
 *
 * UNIFIED QUERY PATH:
 * - Feed results: rpc_discovery_best_matches (with tier/score/flags)
 * - Facets: rpc_get_discover_facets_v3 (progressive disclosure)
 */
export function useDiscoverDeals(): UseDiscoverDealsResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [deals, setDeals] = useState<DiscoverDeal[]>([]);
  const [facets, setFacets] = useState<FacetResponse>({
    subjects: [],
    geo: [],
    brand_categories: [],
    psychographics: [],
    brand_geographic_focus: [],
    total: 0,
  });
  const [facetsV3, setFacetsV3] = useState<FacetResponseV3 | null>(null);
  const [allIndustries, setAllIndustries] = useState<NormalizedIndustryOption[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useV3, setUseV3] = useState(FORCE_V3);
  const [coverage, setCoverage] = useState<TaxonomyCoverage | null>(null);
  const [v3FiltersStripped, setV3FiltersStripped] = useState(false);

  const didStripRef = useRef(false);

  // Parse filters from URL
  const filters = useMemo(() => {
    return searchParamsToFilters(searchParams);
  }, [searchParams]);

  // Update URL when filters change
  const setFilters = useCallback(
    (newFilters: DiscoverFilters) => {
      const params = filtersToSearchParams(newFilters);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const handleUpdateFilter = useCallback(
    <K extends keyof DiscoverFilters>(key: K, value: DiscoverFilters[K]) => {
      setFilters(updateFilter(filters, key, value));
    },
    [filters, setFilters],
  );

  const handleToggleArrayFilter = useCallback(
    (
      key:
        | 'subjects'
        | 'geo'
        | 'brand_categories'
        | 'psychographics'
        | 'brand_geographic_focus'
        | 'industry_ids'
        | 'category_ids'
        | 'subcategory_ids',
      value: string,
    ) => {
      setFilters(toggleArrayFilter(filters, key, value));
    },
    [filters, setFilters],
  );

  const handleResetFilters = useCallback(() => {
    setV3FiltersStripped(false);
    didStripRef.current = false;
    setFilters(resetFilters());
  }, [setFilters]);

  // Fetch all industries once on mount
  useEffect(() => {
    getAllIndustries(supabase).then(setAllIndustries);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get auth user for adoption filtering
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      // Get user's katalog for adoption status
      let adoptedDealIds = new Set<string>();
      if (authUser?.id) {
        const katalogId = await getUserKatalogId(supabase, authUser.id);
        if (katalogId) {
          adoptedDealIds = await getUserAdoptedDealIds(supabase, katalogId);
        }
      }

      // Get v3 gate status
      const gateResult: V3GateResult = await getV3GateStatus(supabase, filters, {
        force: FORCE_V3,
        threshold: V3_COVERAGE_THRESHOLD,
      });

      setCoverage(gateResult.coverage);
      setUseV3(gateResult.enabled);

      // Build RPC context
      const rpcCtx = buildRpcContext(filters);

      // Target count based on viewport (mobile = 24, desktop = 40)
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const targetCount = isMobile ? 24 : 40;

      // Fetch facets and results in parallel
      const [facetsResult, rpcResult] = await Promise.all([
        getDiscoverFacetsV3(supabase, filters),
        supabase.rpc('rpc_discovery_best_matches', {
          ctx: rpcCtx,
          target_count: targetCount,
        }),
      ]);

      // Set facets
      setFacets({
        subjects: facetsResult.subjects || [],
        geo: facetsResult.geo || [],
        brand_categories: facetsResult.brand_categories || [],
        psychographics: facetsResult.psychographics || [],
        brand_geographic_focus: facetsResult.brand_geographic_focus || [],
        total: facetsResult.total,
      });
      setFacetsV3(facetsResult);

      // Handle RPC result
      if (rpcResult.error) {
        console.error('[useDiscoverDeals] RPC error:', rpcResult.error);
        setError(rpcResult.error.message);
        setDeals([]);
        setTotal(0);
        return;
      }

      // Map RPC rows to UI deals
      const rpcData = rpcResult.data || [];
      const mappedDeals = rpcData
        .map((row: any) => mapRpcRowToUI(row, adoptedDealIds.has(row.deal_id)))
        .filter((deal: DiscoverDeal) => !deal.isJoined);

      // Preserve exact fulfilling BrandStore identity through the existing
      // public read-only Deal-card metadata contract. The parent Brand
      // handle remains authoritative from discovery; never invent one.
      const dealIdsForStoreIdentity = mappedDeals.map((deal: DiscoverDeal) => deal.id);

      if (dealIdsForStoreIdentity.length > 0) {
        const { data: storeIdentityRows, error: storeIdentityError } =
          await supabase.rpc('resolve_public_deal_card_metadata_v2', {
            p_deal_ids: dealIdsForStoreIdentity,
          });

        if (storeIdentityError) {
          console.warn(
            '[useDiscoverDeals] public BrandStore identity lookup failed',
            storeIdentityError,
          );
        } else {
          const storeIdentityByDeal = new Map<
            string,
            {
              id: string | null;
              slug: string | null;
              displayName: string | null;
            }
          >(
            ((storeIdentityRows ?? []) as any[]).map((row) => [
              row.deal_id,
              {
                id: row.brand_store_id ?? null,
                slug: row.brand_store_slug ?? null,
                displayName: row.brand_store_display_name ?? null,
              },
            ]),
          );

          mappedDeals.forEach((deal: DiscoverDeal) => {
            const identity = storeIdentityByDeal.get(deal.id);
            if (!identity) return;

            deal.brandStoreId = identity.id ?? deal.brandStoreId ?? null;
            deal.brandStoreSlug = identity.slug ?? deal.brandStoreSlug ?? null;
            deal.storeName = identity.displayName ?? deal.storeName;
          });
        }
      }

      // Enrich deals with taxonomy names using explicit join query
      if (mappedDeals.length > 0) {
        const dealIds = mappedDeals.map((d: DiscoverDeal) => d.id);
        const { data: taxRows, error: taxError } = await supabase
          .from('deal_taxonomy')
          .select(`
            deal_id,
            industry_id,
            category_id,
            subcategory_id
          `)
          .in('deal_id', dealIds);

        if (taxError) {
          console.warn('[useDiscoverDeals] Taxonomy fetch error:', taxError.message);
        }

        if (taxRows && taxRows.length > 0) {
          // Collect unique IDs for batch lookup
          const industryIds = new Set<string>();
          const categoryIds = new Set<string>();
          const subcategoryIds = new Set<string>();
          for (const row of taxRows) {
            if (row.industry_id) industryIds.add(row.industry_id);
            if (row.category_id) categoryIds.add(row.category_id);
            if (row.subcategory_id) subcategoryIds.add(row.subcategory_id);
          }

          // Batch fetch labels
          const [indRes, catRes, subRes] = await Promise.all([
            industryIds.size > 0
              ? supabase.from('taxonomy_industries').select('id, label').in('id', [...industryIds])
              : Promise.resolve({ data: [] }),
            categoryIds.size > 0
              ? supabase.from('taxonomy_categories').select('id, label').in('id', [...categoryIds])
              : Promise.resolve({ data: [] }),
            subcategoryIds.size > 0
              ? supabase.from('taxonomy_subcategories').select('id, label').in('id', [...subcategoryIds])
              : Promise.resolve({ data: [] }),
          ]);

          const indMap = new Map((indRes.data || []).map((r: any) => [r.id, r.label]));
          const catMap = new Map((catRes.data || []).map((r: any) => [r.id, r.label]));
          const subMap = new Map((subRes.data || []).map((r: any) => [r.id, r.label]));

          // Build deal -> taxonomy label map
          const taxMap = new Map<string, { industry?: string; category?: string; subcategory?: string }>();
          for (const row of taxRows) {
            const existing = taxMap.get(row.deal_id) || {};
            if (row.industry_id) existing.industry = indMap.get(row.industry_id);
            if (row.category_id) existing.category = catMap.get(row.category_id);
            if (row.subcategory_id) existing.subcategory = subMap.get(row.subcategory_id);
            taxMap.set(row.deal_id, existing);
          }

          // Enrich each deal
          for (const deal of mappedDeals) {
            const tax = taxMap.get(deal.id);
            if (tax) {
              deal.industryName = tax.industry ?? null;
              deal.categoryName = tax.category ?? null;
              deal.subcategoryName = tax.subcategory ?? null;
            }
          }

          // Debug: log first 5 enriched deals (gated)
          const q = filters.q?.toLowerCase() || '';
          if (q.includes('snowboard')) {
            debugLog('DISCOVER_TAXONOMY_ENRICHMENT', mappedDeals.slice(0, 5).map((d: DiscoverDeal) => ({
              id: d.id, title: d.title,
              industryName: d.industryName, categoryName: d.categoryName, subcategoryName: d.subcategoryName,
            })));
          }
        }
      }

      // CLIENT-SIDE RANGE FILTERING
      // The RPC's Tier 5 (fill) ignores range filters, so we must enforce them here
      // against the canonical money fields computed in mapRpcRowToUI
      let filteredDeals = mappedDeals;
      const pMin = filters.price_min;
      const pMax = filters.price_max;
      const cMin = filters.commission_min;
      const cMax = filters.commission_max;
      const dMin = filters.discount_min;
      const dMax = filters.discount_max;
      const hasRangeFilters = pMin != null || pMax != null || cMin != null || cMax != null || dMin != null || dMax != null;

      if (hasRangeFilters) {
        const beforeCount = filteredDeals.length;
        filteredDeals = filteredDeals.filter((deal: DiscoverDeal) => {
          // Price filter against effectivePriceAmount
          if (pMin != null && (deal.effectivePriceAmount == null || deal.effectivePriceAmount < pMin)) return false;
          if (pMax != null && (deal.effectivePriceAmount == null || deal.effectivePriceAmount > pMax)) return false;
          // Commission filter against creatorEarningsAmount ($)
          if (cMin != null && (deal.creatorEarningsAmount == null || deal.creatorEarningsAmount < cMin)) return false;
          if (cMax != null && (deal.creatorEarningsAmount == null || deal.creatorEarningsAmount > cMax)) return false;
          // Discount filter against discountAmount ($)
          if (dMin != null && (deal.discountAmount == null || deal.discountAmount < dMin)) return false;
          if (dMax != null && (deal.discountAmount == null || deal.discountAmount > dMax)) return false;
          return true;
        });
        debugLog('DISCOVER_RANGE_FILTER', { pMin, pMax, cMin, cMax, dMin, dMax, before: beforeCount, after: filteredDeals.length });
      }

      setDeals(filteredDeals);
      setTotal(filteredDeals.length);
    } catch (err) {
      console.error('[useDiscoverDeals] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [filters, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute relaxed results info
  const hasRelaxedResults = useMemo(() => {
    return deals.some(
      (d) => d.matchTier > 0 || d.outsidePrice || d.outsideCommission || d.outsideDiscount,
    );
  }, [deals]);

  const relaxedCount = useMemo(() => {
    return deals.filter((d) => d.matchTier > 0).length;
  }, [deals]);

  return {
    deals,
    facets,
    facetsV3,
    allIndustries,
    total,
    loading,
    error,
    filters,
    useV3,
    coverage,
    v3FiltersStripped,
    hasRelaxedResults,
    relaxedCount,
    setFilters,
    updateFilter: handleUpdateFilter,
    toggleArrayFilter: handleToggleArrayFilter,
    resetFilters: handleResetFilters,
    refetch: fetchData,
  };
}

export default useDiscoverDeals;
